import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredSites, worldSites, seaSites } from '../data/mockData';
import { MapPin, ArrowRight, Filter } from 'lucide-react';

const Explore = () => {
  const [activeTab, setActiveTab] = useState('malaysia');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'War and Conflict', 'Colonial and Social History', 'Social History', 'Cemetery and Memorial'];

  const filteredSites = selectedCategory === 'all' 
    ? featuredSites 
    : featuredSites.filter(site => site.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/penang-war-museum-1.jpg"
            alt="Explore"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <h1 className="heading-1 text-[#dc2626] mb-6 max-w-3xl">
            Explore Dark Tourism Sites
          </h1>
          <p className="body-large text-white/90 max-w-3xl">
            Discover places of memory, tragedy, and historical significance across Malaysia and around the world.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-[#0f0f10] border-b border-[#3a2222] sticky top-20 z-40">
        <div className="container-custom">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('malaysia')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'malaysia' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Dark Tourism in Malaysia
            </button>
            <button
              onClick={() => setActiveTab('sea')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'sea' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Southeast Asia
            </button>
            <button
              onClick={() => setActiveTab('world')}
              className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'world' 
                  ? 'text-[#dc2626] border-b-2 border-[#dc2626]' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Around the World
            </button>
          </div>
        </div>
      </section>

      {/* Malaysia Sites */}
      {activeTab === 'malaysia' && (
        <>
          {/* Filter */}
          <section className="space-section bg-[#1a1c1b]">
            <div className="container-custom">
              <div className="flex items-center gap-4 mb-8 flex-wrap">
                <Filter className="text-[#dc2626]" size={24} />
                <span className="text-white font-semibold">Filter by Category:</span>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat
                        ? 'bg-[#dc2626] text-white'
                        : 'bg-[#302f2c] text-white/70 hover:bg-[#3a2222]'
                    }`}
                  >
                    {cat === 'all' ? 'All Sites' : cat}
                  </button>
                ))}
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredSites.map((site) => (
                  <div 
                    key={site.id}
                    className="site-card rounded-lg overflow-hidden group cursor-pointer"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={site.image}
                        alt={site.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#dc2626] text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                          {site.darkLevel}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="caption text-[#dc2626] mb-2 block">{site.category}</span>
                        <h3 className="heading-4 text-white">{site.name}</h3>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3 text-[#888680]">
                        <MapPin size={16} />
                        <span className="body-small">{site.location}</span>
                      </div>
                      <p className="body-small text-white/70 mb-4">
                        {site.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="caption text-[#888680]">{site.typology}</span>
                        <Link 
                          to={`/explore/${site.id}`}
                          className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium inline-flex items-center transition-colors"
                        >
                          Details <ArrowRight className="ml-2" size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Context Section */}
          <section className="space-section bg-[#0f0f10]">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                <h2 className="heading-2 text-[#dc2626] mb-8">Dark Tourism in Malaysia</h2>
                <div className="space-y-6 body-medium text-white/80">
                  <p>
                    Dark tourism in Malaysia exists within a complex cultural, historical, and social landscape. 
                    Unlike some countries where sites of tragedy are clearly marked and actively promoted as 
                    heritage attractions, Malaysia's approach is more cautious and nuanced.
                  </p>
                  
                  <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626]">
                    <h4 className="heading-4 text-white mb-4">Why This Matters</h4>
                    <ul className="space-y-3 body-small text-white/70">
                      <li>• Many sites remain marginally recognized or underinterpreted</li>
                      <li>• Strong cultural and religious sensitivities shape engagement</li>
                      <li>• Popular media often frames these sites through supernatural narratives</li>
                      <li>• Over 250 sites identified through research, many fading or forgotten</li>
                    </ul>
                  </div>

                  <p>
                    This platform documents these places not to sensationalize them, but to preserve their 
                    histories and encourage respectful, educational engagement with Malaysia's complex past.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Southeast Asia Sites */}
      {activeTab === 'sea' && (
        <section className="space-section bg-[#1a1c1b]">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="heading-2 text-[#dc2626] mb-6">Dark Tourism in Southeast Asia</h2>
              <p className="body-medium text-white/80">
                Southeast Asia's history is marked by war, colonial encounters, migration, and rapid social 
                change. The region holds some of the most significant dark tourism sites globally, attracting 
                millions of visitors annually who seek to understand these difficult histories.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {seaSites.map((site, index) => (
                <div 
                  key={index}
                  className="site-card rounded-lg overflow-hidden group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={site.image}
                      alt={site.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="heading-4 text-white">{site.name}</h3>
                      <div className="flex items-center gap-2 mt-2 text-[#dc2626]">
                        <MapPin size={16} />
                        <span className="body-small">{site.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="body-small text-white/70">
                      {site.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* World Sites */}
      {activeTab === 'world' && (
        <section className="space-section bg-[#1a1c1b]">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="heading-2 text-[#dc2626] mb-6">Famous Dark Tourism Sites Around the World</h2>
              <p className="body-medium text-white/80">
                Dark tourism is a global phenomenon. Millions of people each year visit sites connected to 
                death, tragedy, and difficult histories. These locations serve as powerful reminders of 
                human resilience, the cost of conflict, and the importance of remembrance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {worldSites.map((site, index) => (
                <div 
                  key={index}
                  className="site-card rounded-lg overflow-hidden group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={site.image}
                      alt={site.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="heading-4 text-white">{site.name}</h3>
                      <div className="flex items-center gap-2 mt-2 text-[#dc2626]">
                        <MapPin size={16} />
                        <span className="body-small">{site.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="body-small text-white/70">
                      {site.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Explore;
